import * as THREE from "three";
import { getViewAxes, projectWorldToViewportMm } from "./viewProjection";

function dot3(a, b) {
  return (
    Number(a.x || 0) * Number(b.x || 0) +
    Number(a.y || 0) * Number(b.y || 0) +
    Number(a.z || 0) * Number(b.z || 0)
  );
}

function vec3FromThree(v) {
  return {
    x: Number(v.x.toFixed(3)),
    y: Number(v.y.toFixed(3)),
    z: Number(v.z.toFixed(3)),
  };
}

function getAxisFromNormal(normal) {
  const ax = Math.abs(normal.x);
  const ay = Math.abs(normal.y);
  const az = Math.abs(normal.z);

  if (ax >= ay && ax >= az) {
    return {
      axis: "x",
      side: normal.x >= 0 ? "max" : "min",
      label: normal.x >= 0 ? "right face" : "left face",
    };
  }

  if (ay >= ax && ay >= az) {
    return {
      axis: "y",
      side: normal.y >= 0 ? "max" : "min",
      label: normal.y >= 0 ? "top face" : "bottom face",
    };
  }

  return {
    axis: "z",
    side: normal.z >= 0 ? "max" : "min",
    label: normal.z >= 0 ? "front face" : "back face",
  };
}

function getObjectWorldBox(object) {
  if (!object) return null;

  object.updateWorldMatrix(true, true);

  const box = new THREE.Box3().setFromObject(object);
  if (box.isEmpty()) return null;

  return box;
}

function getBoxFaceCoordinate(box, axis, side) {
  if (axis === "x") return side === "max" ? box.max.x : box.min.x;
  if (axis === "y") return side === "max" ? box.max.y : box.min.y;
  return side === "max" ? box.max.z : box.min.z;
}

function getBoxCenterOnFace(box, axis, side) {
  const center = new THREE.Vector3();
  box.getCenter(center);

  const coordinate = getBoxFaceCoordinate(box, axis, side);

  if (axis === "x") center.x = coordinate;
  if (axis === "y") center.y = coordinate;
  if (axis === "z") center.z = coordinate;

  return center;
}

export function buildVisibleFaceReferencesFromObject({
  object,
  nodeId,
  view,
}) {
  const box = getObjectWorldBox(object);
  if (!box || !nodeId) return [];

  const faces = [
    {
      axis: "x",
      side: "min",
      normal: { x: -1, y: 0, z: 0 },
      label: "left face",
    },
    {
      axis: "x",
      side: "max",
      normal: { x: 1, y: 0, z: 0 },
      label: "right face",
    },
    {
      axis: "y",
      side: "min",
      normal: { x: 0, y: -1, z: 0 },
      label: "bottom face",
    },
    {
      axis: "y",
      side: "max",
      normal: { x: 0, y: 1, z: 0 },
      label: "top face",
    },
    {
      axis: "z",
      side: "min",
      normal: { x: 0, y: 0, z: -1 },
      label: "back face",
    },
    {
      axis: "z",
      side: "max",
      normal: { x: 0, y: 0, z: 1 },
      label: "front face",
    },
  ];

  return faces
    .map((face) => {
      const faceRole = getFaceRoleInView(face.normal, view);

      // Face okrenut ka kameri se vidi kao površina i ne nudimo ga za kotiranje.
      if (!faceRole) return null;

      const stablePoint = getBoxCenterOnFace(box, face.axis, face.side);
      const coordinate = getBoxFaceCoordinate(box, face.axis, face.side);

      return {
        id: `face_${nodeId}_${face.axis}_${face.side}`,
        kind: "face",
        nodeId,
        axis: face.axis,
        side: face.side,
        label: face.label,
        coordinate: Number(coordinate.toFixed(3)),
        point: vec3FromThree(stablePoint),
        normal: face.normal,
        dimensionOrientation: faceRole.dimensionOrientation,
        viewAxis: faceRole.viewAxis,
      };
    })
    .filter(Boolean);
}

export function getReferencePickDistanceMm({
  ref,
  view,
  viewport,
  pointerMm,
}) {
  const projected = projectWorldToViewportMm(ref.point, view, viewport);

  // Horizontal dimension reference je face koji se u 2D vidi kao vertikalna linija.
  // Zato merimo udaljenost kursora po X.
  if (ref.dimensionOrientation === "horizontal") {
    return Math.abs(Number(pointerMm.x || 0) - projected.x);
  }

  // Vertical dimension reference je face koji se u 2D vidi kao horizontalna linija.
  // Zato merimo udaljenost kursora po Y.
  return Math.abs(Number(pointerMm.y || 0) - projected.y);
}

function normalizeVec3(v) {
  const x = Number(v?.x || 0);
  const y = Number(v?.y || 0);
  const z = Number(v?.z || 0);

  const len = Math.sqrt(x * x + y * y + z * z) || 1;

  return {
    x: x / len,
    y: y / len,
    z: z / len,
  };
}

function absDot(a, b) {
  return Math.abs(dot3(a, b));
}

function getFaceRoleInView(normal, view) {
  const axes = getViewAxes(view);
  const n = normalizeVec3(normal);

  const towardCamera = absDot(n, axes.n);
  const alongU = absDot(n, axes.u);
  const alongV = absDot(n, axes.v);

  // Face koji gleda ka kameri se vidi kao površina.
  // Ne koristimo ga za 2D kotiranje.
  if (towardCamera > 0.7) {
    return null;
  }

  // Face čija je normala po U osi daje referencu za horizontalnu kotu.
  // Npr. front view: left/right face normal ±X => horizontalna dimenzija.
  if (alongU >= alongV) {
    return {
      dimensionOrientation: "horizontal",
      viewAxis: "u",
    };
  }

  // Face čija je normala po V osi daje referencu za vertikalnu kotu.
  // Npr. front view: top/bottom face normal ±Y => vertikalna dimenzija.
  return {
    dimensionOrientation: "vertical",
    viewAxis: "v",
  };
}

/**
 * Build a stable face reference from a raycast hit.
 * This does NOT use raw hit.point as the dimension reference.
 * It resolves the whole object's world box and snaps to the hit face plane.
 */
export function buildStableFaceReferenceFromHit({
  hit,
  nodeId,
  view,
}) {
  if (!hit?.object || !hit?.face || !hit?.point || !nodeId) return null;

  let worldNormal = null;

  if (hit.face?.normal && hit.object?.matrixWorld) {
    worldNormal = hit.face.normal
      .clone()
      .transformDirection(hit.object.matrixWorld)
      .normalize();
  }

  if (!worldNormal) return null;

  const faceRole = getFaceRoleInView(worldNormal, view);

if (!faceRole) {
  return null;
}

  const { axis, side, label } = getAxisFromNormal(worldNormal);
  const box = getObjectWorldBox(hit.object);

  if (!box) {
    return {
  id: `face_${nodeId}_${axis}_${side}`,
  kind: "face",
  nodeId,
  axis,
  side,
  label,
  coordinate:
    axis === "x"
      ? Number(hit.point.x.toFixed(3))
      : axis === "y"
        ? Number(hit.point.y.toFixed(3))
        : Number(hit.point.z.toFixed(3)),
  point: vec3FromThree(hit.point),
  normal: vec3FromThree(worldNormal),
  dimensionOrientation: faceRole.dimensionOrientation,
  viewAxis: faceRole.viewAxis,
};
  }

  const stablePoint = getBoxCenterOnFace(box, axis, side);
  const coordinate = getBoxFaceCoordinate(box, axis, side);

  return {
  id: `face_${nodeId}_${axis}_${side}`,
  kind: "face",
  nodeId,
  axis,
  side,
  label,
  coordinate: Number(coordinate.toFixed(3)),
  point: vec3FromThree(stablePoint),
  normal: vec3FromThree(worldNormal),
  dimensionOrientation: faceRole.dimensionOrientation,
  viewAxis: faceRole.viewAxis,
};
}

export function getDimensionOrientationFromRefs(ref1, ref2, view) {
  const axes = getViewAxes(view);
  const p1 = ref1.point;
  const p2 = ref2.point;

  const d = {
    x: Number(p2.x || 0) - Number(p1.x || 0),
    y: Number(p2.y || 0) - Number(p1.y || 0),
    z: Number(p2.z || 0) - Number(p1.z || 0),
  };

  const du = Math.abs(dot3(d, axes.u));
  const dv = Math.abs(dot3(d, axes.v));

  return du >= dv ? "horizontal" : "vertical";
}

export function getDimensionValueFromRefs(ref1, ref2, view, orientation) {
  const axes = getViewAxes(view);
  const p1 = ref1.point;
  const p2 = ref2.point;

  const d = {
    x: Number(p2.x || 0) - Number(p1.x || 0),
    y: Number(p2.y || 0) - Number(p1.y || 0),
    z: Number(p2.z || 0) - Number(p1.z || 0),
  };

  return Math.abs(
    orientation === "horizontal"
      ? dot3(d, axes.u)
      : dot3(d, axes.v)
  );
}